// usePodcastAutoRefresh — checks subscribed feeds for newer episodes and
// reacts according to per-feed cadence settings:
//
//   • 'daily'  — checked once per local day on first load. If the feed has
//                a newer top episode than what we last saw, it gets
//                promoted to the top of preferences.podcast_feeds and any
//                playing/queued instance is swapped to the fresh episode
//                (even if the older one wasn't finished — that's the
//                whole point of the morning briefing being fresh).
//   • 'hourly' — checked on a 4-minute wall-clock interval while the app
//                is open. Same swap behavior on a fresher episode.
//   • 'off'    — never auto-checks (default).
//
// A single global toggle in preferences.podcast_settings.auto_refresh_enabled
// halts all scheduled checks without touching per-feed cadence. Default true.
//
// The hook lives at the App shell so timers run regardless of which Pronto
// tab is active. JS timers pause when a mobile browser is fully backgrounded
// — they resume when the user returns. Audio playback itself is unaffected.

import { useEffect, useRef, useCallback } from "react";
import { fetchPodcastFeed } from "../lib/podcasts";

const HOURLY_INTERVAL_MS = 4 * 60 * 1000;
const DAILY_FLAG_KEY = "cucina_v1:podcast_daily_check_day";

function todayLocalKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function usePodcastAutoRefresh({ store, player }) {
  const feeds = store?.preferences?.podcast_feeds || [];
  const settings = store?.preferences?.podcast_settings || {};
  const enabled = settings.auto_refresh_enabled !== false; // default ON

  // Track the last episode id we've observed per feed so we can detect a
  // fresh drop without re-running side effects on every render.
  const seenRef = useRef({});
  const storeRef = useRef(store);
  const playerRef = useRef(player);
  useEffect(() => { storeRef.current = store; }, [store]);
  useEffect(() => { playerRef.current = player; }, [player]);

  // Refetch one feed and react to a newer episode according to opts.
  const checkFeed = useCallback(async (feed, opts) => {
    if (!feed?.url) return;
    let data;
    try { data = await fetchPodcastFeed(feed.url); }
    catch { return; } // swallow — auto-refresh shouldn't surface errors
    const newest = data?.episodes?.[0];
    if (!newest?.audioUrl) return;

    const previous = seenRef.current[feed.id];
    const isFirstSeen = previous === undefined;
    seenRef.current[feed.id] = newest.id;

    // Backfill on first sight: just record the latest id, don't disrupt
    // anything. We can only call something "new" once we've seen it before.
    if (isFirstSeen) return;
    if (previous === newest.id) return;

    // Hydrate canonical title/image into the feed record if available.
    const enrichedFeed = {
      ...feed,
      title: feed.title || data.feed?.title || feed.title,
      image: feed.image || data.feed?.image || feed.image,
    };

    // Swap any currently-playing / queued instance to the fresh episode.
    playerRef.current?.replaceInQueueByFeed?.(feed.id, { episode: newest, feed: enrichedFeed });

    // Promote-to-top behavior: only requested for daily-cadence feeds, and
    // only if the feed isn't already at index 0. Avoids thrashing the order.
    if (opts.promoteToTop) {
      const currentFeeds = storeRef.current?.preferences?.podcast_feeds || [];
      const idx = currentFeeds.findIndex(f => f.id === feed.id);
      if (idx > 0) {
        const next = [...currentFeeds];
        const [moved] = next.splice(idx, 1);
        next.unshift(moved);
        try { await storeRef.current?.setPreferences?.({ ...storeRef.current.preferences, podcast_feeds: next }); }
        catch {}
      }
    }
  }, []);

  // Daily check: run once per local day, on first mount where we have feeds.
  useEffect(() => {
    if (!enabled) return;
    if (feeds.length === 0) return;
    const today = todayLocalKey();
    const lastDay = (() => { try { return localStorage.getItem(DAILY_FLAG_KEY); } catch { return null; } })();
    const dailyFeeds = feeds.filter(f => f.auto_refresh === "daily");
    if (dailyFeeds.length === 0) {
      // Even if no daily feeds, still seed seenRef from any first-time feeds
      // so the eventual hourly check can detect "newer than what we have."
      hydrateSeen(feeds, seenRef.current);
      return;
    }

    // First hydrate seenRef so we don't false-positive on first load: the
    // very first refetch will set seenRef without triggering swaps.
    if (Object.keys(seenRef.current).length === 0) hydrateSeen(feeds, seenRef.current);

    if (lastDay === today) return;
    try { localStorage.setItem(DAILY_FLAG_KEY, today); } catch {}

    (async () => {
      for (const f of dailyFeeds) {
        await checkFeed(f, { promoteToTop: true });
      }
    })();
  }, [enabled, feeds, checkFeed]);

  // Hourly check: every 4 minutes refetch all 'hourly' feeds.
  useEffect(() => {
    if (!enabled) return;
    const hourlyFeeds = feeds.filter(f => f.auto_refresh === "hourly");
    if (hourlyFeeds.length === 0) return;

    if (Object.keys(seenRef.current).length === 0) hydrateSeen(feeds, seenRef.current);

    const tick = async () => {
      for (const f of hourlyFeeds) {
        await checkFeed(f, { promoteToTop: false });
      }
    };
    const id = setInterval(tick, HOURLY_INTERVAL_MS);
    return () => clearInterval(id);
  }, [enabled, feeds, checkFeed]);
}

// Best-effort: pre-populate seenRef from any existing latest_episode_id we
// might have stashed on the feed records. Not critical — we'll backfill on
// the first checkFeed call regardless.
function hydrateSeen(feeds, into) {
  for (const f of feeds) {
    if (f.latest_episode_id && !(f.id in into)) into[f.id] = f.latest_episode_id;
  }
}
